import React, { useState } from "react";
import {
  Grid,
  Typography,
  CircularProgress,
  Card,
  CardContent,
  Avatar,
  Box,
} from "@mui/material";
import axios from "axios";
import { StyledTextField } from "../doctor-console/StyledComponents";
import { DataGrid } from "@mui/x-data-grid";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { toast } from "react-toastify";

const MedicinesManagement = () => {
  const [searchValue, setSearchValue] = useState("");
  const [loading, setLoading] = useState(false);
  const [patient, setPatient] = useState(null);
  const [prescriptions, setPrescriptions] = useState([]);
  const [selectedPrescription, setSelectedPrescription] = useState(null);
  const [error, setError] = useState("");
  const REACT_APP_SUFFIX =
    process.env.REACT_APP_IS_ABDM_CONNECTED_WITH_PROD === "true"
      ? process.env.REACT_APP_SUFFIX_PR
      : process.env.REACT_APP_SUFFIX_SB;
  const fetchPrescriptions = async () => {
    if (!searchValue) return;

    const isPhone = /^\d{10}$/.test(searchValue);
    const endsWithSuffix = searchValue.endsWith(REACT_APP_SUFFIX);

    if (!isPhone && !endsWithSuffix) {
      setError("true");
      toast.error(
        `Invalid input! Must be a 10-digit phone number or end with ${REACT_APP_SUFFIX}`
      );
      return; // stop execution if invalid
    } else {
      setError("");
    }
    setLoading(true);
    try {
      // Determine if input is phone (10-digit number) or ABHA
      const isPhone = /^\d{10}$/.test(searchValue);
      const payload = isPhone
        ? { phone_no: searchValue }
        : { abha_address: searchValue };

      const { data } = await axios.post(
        `${process.env.REACT_APP_BACKEND_ROUTE_URL}get_patient_prescription_details/`,
        payload
      );

      if (
        data?.status === "Success" &&
        data?.patient_prescriptions?.length > 0
      ) {
        const patientData = data.patient_prescriptions[0];

        const normalizedPrescriptions = patientData.prescriptions.map((p) => ({
          ...p,
          prescription: {
            ...p.prescription,
            chief_complaints: Array.isArray(p.prescription.chief_complaints)
              ? p.prescription.chief_complaints
              : p.prescription.chief_complaints
              ? [p.prescription.chief_complaints]
              : [],
            medications: Array.isArray(p.prescription.medications)
              ? p.prescription.medications
              : p.prescription.medications
              ? [p.prescription.medications]
              : [],
          },
        }));

        setPatient(patientData.prescriptions[0].patient);
        setPrescriptions(normalizedPrescriptions);
        setSelectedPrescription(normalizedPrescriptions[0]);
      } else {
        setPatient(null);
        setPrescriptions([]);
        setSelectedPrescription(null);
        toast.error(data.message);
      }
    } catch (error) {
      console.error("Error fetching prescriptions:", error);
    } finally {
      setLoading(false);
    }
  };

  const formatDateTime = (datetime) => {
    if (!datetime) return "N/A";
    const dateObj = new Date(datetime);
    return dateObj.toLocaleString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };
  console.log("error", error);
  // Determine placeholder dynamically
  // const placeholderText = /^\d*$/.test(searchValue)
  //   ? "Enter Phone Number"
  //   : "Enter ABHA Address";
  const placeholderText = "Enter Phone Number or ABHA Address";
  return (
    <Box sx={{ minHeight: "100vh", px: { xs: 2, sm: 4, md: 6 }, py: 2 }}>
      <Grid
        container
        spacing={2}
        sx={{ px: { xs: 2, md: 7 } }}
        alignItems="stretch"
      >
        {/* Search Bar */}
        <Grid item xs={12} mb={4}>
          <Typography
            variant="h5"
            sx={{ fontWeight: 700, mb: 2, color: "#002D62", ml: 2 }}
          >
            Search Patient
          </Typography>
          <Card
            sx={{
              p: 2,
              mb: 3,
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              gap: 2,
              alignItems: "center",
              borderRadius: "12px",
              boxShadow: "0px 2px 6px rgba(0,0,0,0.08)",
            }}
          >
            <StyledTextField
              error={!!error}
              placeholder={placeholderText}
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
              sx={{
                flex: 1,
                "& .MuiOutlinedInput-root": {
                  borderRadius: "8px",
                  bgcolor: "#e6f0fb",
                },
              }}
            />

            <button
              className="btn btn__secondary"
              onClick={fetchPrescriptions}
              disabled={loading || !searchValue}
              style={{ width: "120px", alignSelf: "stretch" }}
            >
              {loading ? (
                <CircularProgress size={20} sx={{ color: "#fff" }} />
              ) : (
                "SEARCH"
              )}
            </button>
          </Card>
        </Grid>

        {/* Left Column */}
        <Grid
          item
          xs={12}
          md={6}
          sx={{ display: "flex", flexDirection: "column" }}
        >
          {patient ? (
            <Card
              sx={{
                flexShrink: 0,
                p: 2,
                mb: 3,
                display: "flex",
                alignItems: "center",
                borderRadius: "12px",
                boxShadow: "0px 6px 20px rgba(0,0,0,0.25)",
              }}
            >
              <Avatar
                sx={{ width: 64, height: 64, mr: 2, bgcolor: "#e0e0e0" }}
              />
              <CardContent sx={{ flex: 1, p: 0 }}>
                <Grid container justifyContent="space-between">
                  <Typography variant="h6" sx={{ fontWeight: 700 }}>
                    {patient.patient_name || "N/A"}
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {patient.patient_gender || "N/A"},{" "}
                    {patient.patient_age ?? "N/A"} yrs
                  </Typography>
                </Grid>
                <Typography variant="body2" sx={{ mt: 1 }}>
                  {patient.patient_address || "N/A"}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Pincode: {patient.patient_pincode || "N/A"}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Phone: {patient.patient_phone_no || "N/A"}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  ABHA: {patient.patient_abha_id || "N/A"}
                </Typography>
              </CardContent>
            </Card>
          ) : (
            <Typography variant="body2" color="text.secondary"></Typography>
          )}

          {/* Records Date */}
          {prescriptions.length > 0 && (
            <Card
              sx={{
                flex: 1,
                p: 2,
                borderRadius: "12px",
                boxShadow: "0px 2px 6px rgba(0,0,0,0.08)",
                display: "flex",
                flexDirection: "column",
              }}
            >
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                Records Date:
              </Typography>
              <Box sx={{ flex: 1, minHeight: { xs: 300, md: 400 } }}>
                <DataGrid
                  rows={prescriptions.map((p, i) => ({
                    id: i,
                    datetime: formatDateTime(p.prescription.datetime),
                    doctor: p.practitioner?.name || "N/A",
                    raw: p,
                  }))}
                  columns={[
                    {
                      field: "datetime",
                      headerName: "Date",
                      flex: 1,
                      headerAlign: "center",
                      align: "center",
                    },
                    {
                      field: "doctor",
                      headerName: "Doctor",
                      flex: 1,
                      headerAlign: "center",
                      align: "center",
                      renderCell: (params) => (
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "flex-end",
                            width: "100%",
                          }}
                        >
                          <span
                            style={{
                              marginRight: 6,
                              fontWeight: 600,
                              color: "#002D62",
                            }}
                          >
                            {params.value}
                          </span>
                          <ChevronRightIcon
                            sx={{ color: "#002D62", fontSize: 22 }}
                          />
                        </div>
                      ),
                    },
                  ]}
                  // pageSize={5}
                  // rowsPerPageOptions={[5]}
                  initialState={{
                    pagination: { paginationModel: { pageSize: 5, page: 0 } },
                  }}
                  pageSizeOptions={[5]}
                  disableColumnMenu
                  disableSelectionOnClick
                  onRowClick={(params) =>
                    setSelectedPrescription(params.row.raw)
                  }
                  getRowClassName={(params) =>
                    selectedPrescription?.prescription?.datetime ===
                    params.row.raw.prescription.datetime
                      ? "Mui-selected"
                      : ""
                  }
                  sx={{
                    "& .MuiDataGrid-columnHeaders": {
                      backgroundColor: "#f5f5f5",
                      fontWeight: "bold",
                    },
                    "& .MuiDataGrid-row.Mui-selected": {
                      backgroundColor: "#e3f2fd !important",
                    },
                    "& .MuiDataGrid-cell": { cursor: "pointer" },
                  }}
                />
              </Box>
            </Card>
          )}
        </Grid>

        {/* Right Column */}
        <Grid
          item
          xs={12}
          md={6}
          sx={{ display: "flex", mt: { xs: 2, md: 0 } }}
        >
          {selectedPrescription ? (
            <Card
              sx={{
                flex: 1,
                p: { xs: 2, md: 4 },
                borderRadius: "16px",
                display: "flex",
                flexDirection: "column",
                justifyContent: "flex-start",
                gap: 2,
                boxShadow: "0px 2px 6px rgba(0,0,0,0.08)",
                bgcolor: "#F0F8F0",
              }}
            >
              {/* Chief Complaints */}
              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mt: 2 }}>
                  Chief Complaints
                </Typography>
                {selectedPrescription.prescription.chief_complaints?.length >
                0 ? (
                  <ul style={{ marginTop: 4 }}>
                    {selectedPrescription.prescription.chief_complaints.map(
                      (c, i) => (
                        <li key={i}>
                          <Typography variant="body2">{c}</Typography>
                        </li>
                      )
                    )}
                  </ul>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    No complaints available
                  </Typography>
                )}
              </Box>

              {/* Medications */}
              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mt: 2 }}>
                  Medications
                </Typography>
                {selectedPrescription.prescription.medications?.length > 0 ? (
                  <ul style={{ marginTop: 4 }}>
                    {selectedPrescription.prescription.medications.map(
                      (m, i) => (
                        <li key={i}>
                          <Box>
                            <Typography variant="body2">{m.name}</Typography>
                            <Typography variant="body2">
                              Dosage: {m.dosage}
                            </Typography>
                            {m.note && (
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                Note: {m.note}
                              </Typography>
                            )}
                          </Box>
                        </li>
                      )
                    )}
                  </ul>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    No medications available
                  </Typography>
                )}
              </Box>

              {/* Diagnosis */}
              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mt: 2 }}>
                  Diagnosis
                </Typography>
                {selectedPrescription.prescription.diagnosis ? (
                  <ul style={{ marginTop: 4 }}>
                    <li>
                      <Typography variant="body2">
                        {selectedPrescription.prescription.diagnosis}
                      </Typography>
                    </li>
                  </ul>
                ) : (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mt: 1 }}
                  >
                    No diagnosis available
                  </Typography>
                )}
              </Box>
            </Card>
          ) : (
            <Typography>No record selected</Typography>
          )}
        </Grid>
      </Grid>
    </Box>
  );
};

export default MedicinesManagement;
